import React, { useState, useEffect } from "react";
import { profileService } from "../services/profileService";
import { blobToBase64 } from "../utils/imageHelpers";

const ReviewList = ({ reviews = [], onAddComment, pagination, onPageChange }) => {
  const [comment, setComment] = useState("");
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [userImages, setUserImages] = useState({});

  useEffect(() => {
    if (!reviews.length) return;
    const emails = [...new Set([
      ...reviews.map((r) => r.email).filter(Boolean),
      ...reviews.flatMap((r) => (r.comments || []).map((c) => c.email)).filter(Boolean),
    ])];
    const missing = emails.filter((e) => !(e in userImages));
    if (!missing.length) return;
    const fetchImages = async () => {
      const images = {};
      await Promise.all(
        missing.map(async (email) => {
          try {
            const response = await profileService.getImageByEmail(email);
            images[email] = await blobToBase64(response.data);
          } catch {
            images[email] = null;
          }
        })
      );
      setUserImages((prev) => ({ ...prev, ...images }));
    };
    fetchImages();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviews]);

  if (!reviews || reviews.length === 0) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-900">Community Reviews</h2>
        <p className="text-gray-500">No reviews yet. Be the first to share!</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Community Reviews</h2>
      
      {reviews.map((review) => (
        <div key={review.id} className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-start">
            <img
              className="h-10 w-10 rounded-full mr-4 object-cover flex-shrink-0"
              src={
                userImages[review.email] ||
                `https://api.dicebear.com/6.x/initials/svg?seed=${review.email}`
              }
              alt={review.user || "User"}
              onError={(e) => {
                e.target.src = `https://api.dicebear.com/6.x/initials/svg?seed=${review.email}`;
              }}
            />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  {review.user || "Anonymous"}
                </h3>
                <span className="text-sm text-gray-500">
                  {review.date || "Unknown date"}
                </span>
              </div>
              
              <div className="flex mt-1">
                {[...Array(5)].map((_, i) => (
                  <svg
                    key={i}
                    className={`h-5 w-5 ${
                      i < (review.rating || 0) ? "text-yellow-400" : "text-gray-300"
                    }`}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              
              <p className="mt-2 text-gray-600">
                {review.text || review.feedback || "No content provided"}
              </p>

              {/* Comment section - only show if actively commenting */}
              {activeCommentId === review.id && (
                <div className="mt-4 pl-6">
                  <div className="flex items-start">
                    <img
                          className="h-8 w-8 rounded-full mr-3 object-cover flex-shrink-0"
                          src={
                            userImages[review.email] ||
                            `https://api.dicebear.com/6.x/initials/svg?seed=${review.email}`
                          }
                          alt={review.user || "User"}
                          onError={(e) => {
                            e.target.src = `https://api.dicebear.com/6.x/initials/svg?seed=${review.email}`;
                          }}
                        />
                    <div className="flex-1">
                      <textarea
                        rows="2"
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border border-gray-300 rounded-md p-2"
                        placeholder="Add a comment..."
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                      />
                      <div className="mt-2 flex space-x-2">
                        <button
                          onClick={() => {
                            onAddComment(review.id, comment);
                            setComment("");
                            setActiveCommentId(null);
                          }}
                          className="px-3 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                        >
                          Post Comment
                        </button>
                        <button
                          onClick={() => setActiveCommentId(null)}
                          className="px-3 py-1 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Render existing comments */}
                {review.comments && review.comments.length > 0 && (
                  <div className="mt-4 space-y-3 pl-6">
                    {review.comments.map((c, index) => (
                      <div key={index} className="flex items-start">
                        <img
                          className="h-8 w-8 rounded-full mr-3 object-cover flex-shrink-0"
                          src={
                            userImages[c.email] ||
                            `https://api.dicebear.com/6.x/initials/svg?seed=${c.email || 'user'}`
                          }
                          alt={c.username || c.email || 'Commenter'}
                          onError={(e) => {
                            e.target.src = `https://api.dicebear.com/6.x/initials/svg?seed=${c.email || 'user'}`;
                          }}
                        />
                        <div className="bg-gray-100 p-3 rounded-lg w-full">
                          <p className="text-sm font-semibold text-gray-700">{c.username || c.email}</p>
                          <p className="text-sm text-gray-600">{c.comment}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

              {/* Show add comment button if not already active */}
              {activeCommentId !== review.id && (
                <button
                  onClick={() => setActiveCommentId(review.id)}
                  className="mt-4 text-sm text-indigo-600 hover:text-indigo-800"
                >
                  Add a comment
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
      {/* Pagination */}
      {pagination && pagination.total > pagination.limit && (
        <div className="flex justify-center mt-8 space-x-2">
          {Array.from({
            length: Math.ceil(pagination.total / pagination.limit),
          }).map((_, i) => (
            <button
              key={i}
              onClick={() => onPageChange(i + 1)}
              className={`px-3 py-1 border rounded ${
                pagination.page === i + 1
                  ? "bg-indigo-600 text-white"
                  : "bg-white text-gray-700 border-gray-300"
              } hover:bg-indigo-500 hover:text-white`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

    </div>
  );
};

export default ReviewList;